import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Checkbox,
    ListItemText,
    Alert,
    Badge,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PaymentsIcon from '@mui/icons-material/Payments';
import StickyNoteIcon from '@mui/icons-material/StickyNote2Outlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import { alpha } from '@mui/material/styles';
import { apiFetch } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import {
    addDays,
    endOfWeek,
    isWeekend,
    payPeriodFor,
    rangeOf,
    startOfWeek,
    today as todayIso,
    type IsoDate,
} from '../lib/dates';
import { EntryDialog } from '../components/timesheet/EntryDialog';
import { LeaveRequestsDialog } from '../components/timesheet/LeaveRequestsDialog';
import { PayrollDialog } from '../components/timesheet/PayrollDialog';
import {
    dayLabel,
    dmy,
    hrs,
    leaveLabel,
    type Entry,
    type Lookups,
    type PayNote,
    type TimesheetStaff,
} from '../components/timesheet/types';

/**
 * Timesheet.
 *
 * The legacy Backend_timesheet screen, laid out like the Jobs page:
 *
 *   * A filter card: Staff Member (or All Staff), the pay fortnight with a
 *     clickable day strip, and the Today / Yesterday / Current Week / Last Week
 *     buttons. Like the legacy screen, it opens on today.
 *   * Entries grouped by staff member, each group with its totals and a
 *     Payroll button (the legacy "Total" screen: category grid, pay notes,
 *     Send to Xero).
 *   * Columns in legacy order: Date, Client, Job Address, Other, Start, Finish,
 *     Total Hours, Lunch — plus overtime, which the legacy row did not show but
 *     payroll turns on.
 *   * Row actions: the staff member's shift note, the day's pay notes, edit,
 *     delete (confirmed).
 *
 * Pay periods are Monday-to-Sunday fortnights; see payPeriodFor.
 */

/** Hours to two places, or nothing — dashes in every empty cell made the table hard to read. */
function blank(n: number | null): string {
    return n === null || n === 0 ? '' : n.toFixed(2);
}

interface PayCycle {
    readonly payrollFirstPayDate: string | null;
    readonly payrollDays: number;
}

/** Used until the company record loads, and if it has no anchor set. */
const FALLBACK_CYCLE = { anchor: '2018-08-16', days: 14 };

type View =
    | { readonly kind: 'day'; readonly date: IsoDate }
    | { readonly kind: 'period' }
    | { readonly kind: 'range'; readonly id: 'week' | 'lastweek'; readonly from: IsoDate; readonly to: IsoDate };

interface Group {
    readonly staffId: number | null;
    readonly name: string;
    readonly entries: readonly Entry[];
}

export function Timesheet(): React.JSX.Element {
    const today = useMemo(() => todayIso(), []);
    const [cycle, setCycle] = useState<{ anchor: IsoDate; days: number }>(FALLBACK_CYCLE);
    const [cursor, setCursor] = useState<IsoDate>(today);
    const [view, setView] = useState<View>({ kind: 'day', date: today });
    /**
     * Which staff the grid shows and Payroll acts on. Empty = everyone, which
     * is what the legacy "All Staff" option meant.
     *
     * A set rather than one id because payroll is run for a crew, not a person:
     * the office sends a whole fortnight to Xero one staff member after another,
     * and picking them one at a time and reopening the dialog for each was the
     * legacy screen's shape, not a requirement.
     */
    const [staffFilter, setStaffFilter] = useState<readonly number[]>([]);
    const [staff, setStaff] = useState<readonly TimesheetStaff[]>([]);
    const [lookups, setLookups] = useState<Lookups | null>(null);
    const [entries, setEntries] = useState<readonly Entry[]>([]);
    const [notes, setNotes] = useState<readonly PayNote[]>([]);
    const [pendingLeave, setPendingLeave] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    const [editing, setEditing] = useState<Entry | 'new' | null>(null);
    const [newFor, setNewFor] = useState<number | null>(null);
    const [deleting, setDeleting] = useState<Entry | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [shiftNote, setShiftNote] = useState<Entry | null>(null);
    const [dayNotes, setDayNotes] = useState<{ entry: Entry; notes: readonly PayNote[] } | null>(null);
    const [payroll, setPayroll] = useState<{ staffIds: readonly number[]; from: IsoDate } | null>(null);
    const [leaveOpen, setLeaveOpen] = useState(false);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    const period = useMemo(() => payPeriodFor(cursor, cycle.anchor, cycle.days), [cursor, cycle]);
    const stripDates = useMemo(() => rangeOf(period.from, period.to), [period]);
    const range = useMemo(
        () =>
            view.kind === 'day'
                ? { from: view.date, to: view.date }
                : view.kind === 'range'
                  ? { from: view.from, to: view.to }
                  : period,
        [view, period],
    );

    useEffect(() => {
        let cancelled = false;
        apiFetch<PayCycle>('/company')
            .then((c) => {
                if (!cancelled && c.payrollFirstPayDate !== null) {
                    setCycle({ anchor: c.payrollFirstPayDate, days: c.payrollDays });
                }
            })
            .catch(() => {
                /* Fall back to the production cycle. */
            });
        Promise.all([
            apiFetch<{ staff: readonly TimesheetStaff[] }>('/timesheets/staff'),
            apiFetch<Lookups>('/timesheets/lookups'),
        ])
            .then(([s, l]) => {
                if (!cancelled) {
                    setStaff(s.staff);
                    setLookups(l);
                }
            })
            .catch(() => {
                /* The pickers stay empty; entries still load. */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        apiFetch<{ pending: number }>('/timesheets/leave-requests/summary')
            .then((r) => {
                if (!cancelled) {
                    setPendingLeave(r.pending);
                }
            })
            .catch(() => {
                /* The badge stays off. */
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        // The endpoint narrows by a single staff member. For one pick that
        // keeps the payload small; for several, ask for everyone and filter
        // below — a range is capped at 62 days, so this is bounded either way.
        const onlyStaff = staffFilter.length === 1 ? staffFilter[0] : undefined;
        const staffParam =
            onlyStaff !== undefined && Number.isInteger(onlyStaff) ? `&staffId=${String(onlyStaff)}` : '';
        Promise.all([
            apiFetch<{ entries: readonly Entry[] }>(`/timesheets?from=${range.from}&to=${range.to}${staffParam}`),
            apiFetch<{ notes: readonly PayNote[] }>(`/timesheets/pay-notes?from=${range.from}&to=${range.to}${staffParam}`),
        ])
            .then(([t, n]) => {
                if (!cancelled) {
                    setEntries(t.entries);
                    setNotes(n.notes);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load timesheets');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [range, staffFilter, nonce]);

    /** Grouped by staff member, alphabetically, like the legacy parent/child rows. */
    const groups = useMemo<readonly Group[]>(() => {
        // One pick is already narrowed by the query; several are narrowed here.
        const wanted = staffFilter.length > 1 ? new Set(staffFilter) : null;
        const byStaff = new Map<string, Entry[]>();
        for (const e of entries) {
            if (wanted !== null && (e.staffId === null || !wanted.has(e.staffId))) {
                continue;
            }
            const key = String(e.staffId);
            byStaff.set(key, [...(byStaff.get(key) ?? []), e]);
        }
        return [...byStaff.values()]
            .map((list) => ({
                staffId: list[0]?.staffId ?? null,
                name: list[0]?.staffName ?? 'No staff member',
                entries: list,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [entries]);

    const notesByStaffDate = useMemo(() => {
        const m = new Map<string, PayNote[]>();
        for (const n of notes) {
            const key = `${String(n.staffId)}:${n.noteDate}`;
            m.set(key, [...(m.get(key) ?? []), n]);
        }
        return m;
    }, [notes]);

    const staffById = useMemo(() => new Map(staff.map((s) => [s.staffId, s])), [staff]);
    /** Just the names, for the payroll dialog's tab labels. */
    const staffNames = useMemo(() => new Map(staff.map((s) => [s.staffId, s.name])), [staff]);

    /**
     * Why Payroll is unavailable, or null. Legacy: "Please Select Staff".
     *
     * Only the "nobody picked" case blocks the button now. Whether an
     * individual can be SENT — paid employee, linked to Xero, has categories —
     * is per person and is shown against their name inside the dialog, because
     * with a crew selected one ineligible person should not stop the other
     * eleven from being reviewed and sent.
     */
    const payrollBlocked = useMemo(
        () => (staffFilter.length === 0 ? 'Tick at least one staff member' : null),
        [staffFilter],
    );
    const multiDay = range.from !== range.to;

    function goPeriod(direction: -1 | 1): void {
        const next = addDays(period.from, direction * cycle.days);
        setCursor(next);
        setView({ kind: 'period' });
    }

    function remove(entry: Entry): void {
        setDeleteBusy(true);
        apiFetch<unknown>(`/timesheets/${String(entry.id)}`, { method: 'DELETE' })
            .then(() => {
                setDeleting(null);
                setEditing(null);
                setNotice('Entry deleted');
                reload();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not delete the entry');
                setDeleting(null);
            })
            .finally(() => {
                setDeleteBusy(false);
            });
    }

    const quick: readonly { id: string; label: string; active: boolean; onClick: () => void }[] = [
        {
            id: 'today',
            label: 'Today',
            active: view.kind === 'day' && view.date === today,
            onClick: () => {
                setCursor(today);
                setView({ kind: 'day', date: today });
            },
        },
        {
            id: 'yesterday',
            label: 'Yesterday',
            active: view.kind === 'day' && view.date === addDays(today, -1),
            onClick: () => {
                const y = addDays(today, -1);
                setCursor(y);
                setView({ kind: 'day', date: y });
            },
        },
        {
            id: 'week',
            label: 'Current Week',
            active: view.kind === 'range' && view.id === 'week',
            onClick: () => {
                setCursor(today);
                setView({ kind: 'range', id: 'week', from: startOfWeek(today), to: endOfWeek(today) });
            },
        },
        {
            id: 'lastweek',
            label: 'Last Week',
            active: view.kind === 'range' && view.id === 'lastweek',
            onClick: () => {
                const from = addDays(startOfWeek(today), -7);
                setCursor(from);
                setView({ kind: 'range', id: 'lastweek', from, to: addDays(from, 6) });
            },
        },
    ];

    const rangeLabel =
        range.from === range.to ? dayLabel(range.from) : `${dmy(range.from)} – ${dmy(range.to)}`;

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Timesheet" subtitle="Hours, leave and payroll by pay period" />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack gap={2}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ flex: 1 }}
                        >
                            <TextField
                                select
                                size="small"
                                label="Staff"
                                value={staffFilter.map(String)}
                                slotProps={{
                                    select: {
                                        multiple: true,
                                        displayEmpty: true,
                                        // The closed field has to stay one line
                                        // on a toolbar, so a long crew reads as
                                        // a count rather than a wall of names.
                                        renderValue: (picked) => {
                                            const ids = picked as string[];
                                            if (ids.length === 0) {
                                                return 'All Staff';
                                            }
                                            if (ids.length === 1) {
                                                return staffById.get(Number(ids[0]))?.name ?? '1 selected';
                                            }
                                            return `${String(ids.length)} staff selected`;
                                        },
                                        onChange: (e) => {
                                            const picked = e.target.value as string[];
                                            // Filtered, not trusted. MUI treats
                                            // EVERY child of a Select as an
                                            // option, so a stray non-MenuItem
                                            // child fires onChange carrying
                                            // `undefined` — which became NaN,
                                            // then `staffId=NaN`, and the API
                                            // answered "staffId must be an
                                            // integer". The children are all
                                            // MenuItems again; this makes it
                                            // impossible to reintroduce.
                                            setStaffFilter(picked.map(Number).filter(Number.isInteger));
                                        },
                                        MenuProps: { slotProps: { paper: { sx: { maxHeight: 420 } } } },
                                    },
                                    inputLabel: { shrink: true },
                                }}
                                sx={{ minWidth: { sm: 260 } }}
                            >
                                {staff.map((s) => (
                                    <MenuItem key={s.staffId} value={String(s.staffId)} sx={{ py: 0.25 }}>
                                        <Checkbox
                                            size="small"
                                            checked={staffFilter.includes(s.staffId)}
                                            sx={{ mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={s.name}
                                            secondary={
                                                s.incomeType === 'SALARYANDWAGES' ? undefined : 'Not a paid employee'
                                            }
                                            slotProps={{
                                                primary: { fontSize: '0.875rem' },
                                                secondary: { fontSize: '0.72rem' },
                                            }}
                                        />
                                    </MenuItem>
                                ))}
                            </TextField>
                            {/* Beside the field rather than inside the dropdown:
                                a Select's children are its options, and these
                                are commands. */}
                            <ButtonGroup size="small" variant="outlined">
                                <Button
                                    disabled={staff.length === 0 || staffFilter.length === staff.length}
                                    onClick={() => {
                                        setStaffFilter(staff.map((x) => x.staffId));
                                    }}
                                >
                                    All
                                </Button>
                                <Button
                                    disabled={staffFilter.length === 0}
                                    onClick={() => {
                                        setStaffFilter([]);
                                    }}
                                >
                                    None
                                </Button>
                            </ButtonGroup>
                            <ButtonGroup size="small" variant="outlined">
                                <Button aria-label="Previous pay period" onClick={() => { goPeriod(-1); }}>
                                    <ChevronLeftIcon fontSize="small" />
                                </Button>
                                <Button
                                    variant={view.kind === 'period' ? 'contained' : 'outlined'}
                                    onClick={() => {
                                        setView({ kind: 'period' });
                                    }}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Pay period {dmy(period.from)} – {dmy(period.to)}
                                </Button>
                                <Button aria-label="Next pay period" onClick={() => { goPeriod(1); }}>
                                    <ChevronRightIcon fontSize="small" />
                                </Button>
                            </ButtonGroup>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                {quick.map((q) => (
                                    <Button
                                        key={q.id}
                                        size="small"
                                        variant={q.active ? 'contained' : 'outlined'}
                                        color={q.active ? 'primary' : 'inherit'}
                                        onClick={q.onClick}
                                    >
                                        {q.label}
                                    </Button>
                                ))}
                                {loading ? <CircularProgress size={18} /> : null}
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Badge color="error" badgeContent={pendingLeave} invisible={pendingLeave === 0}>
                                <Button
                                    variant="outlined"
                                    startIcon={<EventBusyIcon />}
                                    onClick={() => {
                                        setLeaveOpen(true);
                                    }}
                                >
                                    Leave Requests
                                </Button>
                            </Badge>
                            {/* Legacy "Total": one button, for the staff member picked above,
                                and only for paid employees (get_total_page). */}
                            <Tooltip title={payrollBlocked ?? ''}>
                                <span>
                                    <Button
                                        variant="outlined"
                                        color="success"
                                        startIcon={<PaymentsIcon />}
                                        disabled={payrollBlocked !== null}
                                        onClick={() => {
                                            setPayroll({
                                                staffIds: [...staffFilter],
                                                from: payPeriodFor(range.from, cycle.anchor, cycle.days).from,
                                            });
                                        }}
                                    >
                                        Payroll
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setNewFor(staffFilter.length === 1 ? (staffFilter[0] ?? null) : null);
                                    setEditing('new');
                                }}
                            >
                                New Entry
                            </Button>
                        </Stack>
                    </Stack>

                    {/* Day strip: the fortnight, one button per day (legacy day header row). */}
                    <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5 }}>
                        {stripDates.map((d) => {
                            const active = view.kind === 'day' && view.date === d;
                            return (
                                <Button
                                    key={d}
                                    size="small"
                                    variant={active ? 'contained' : 'outlined'}
                                    color={active ? 'primary' : 'inherit'}
                                    onClick={() => {
                                        setView({ kind: 'day', date: d });
                                    }}
                                    sx={{
                                        flex: '1 0 auto',
                                        minWidth: 74,
                                        whiteSpace: 'nowrap',
                                        opacity: isWeekend(d) && !active ? 0.6 : 1,
                                        fontWeight: d === today ? 800 : 500,
                                        borderStyle: d === today && !active ? 'dashed' : undefined,
                                    }}
                                >
                                    {dayLabel(d)}
                                </Button>
                            );
                        })}
                    </Box>
                </Stack>
            </Paper>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={reload}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}
            {notice !== null ? (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setNotice(null);
                    }}
                >
                    {notice}
                </Alert>
            ) : null}

            <Typography sx={{ mb: 1, fontWeight: 700, color: 'text.secondary' }}>
                {rangeLabel}
                {groups.length > 0
                    ? ` · ${String(groups.length)} ${groups.length === 1 ? 'person' : 'people'} · ${String(entries.length)} ${entries.length === 1 ? 'entry' : 'entries'}`
                    : ''}
            </Typography>

            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table
                    size="small"
                    sx={{
                        minWidth: 1080,
                        // The theme divider is almost invisible on the dark background;
                        // these lines are what separate the rows, so they need to show.
                        '& td, & th': {
                            py: 1.1,
                            borderBottom: '1px solid',
                            borderColor: (t) => alpha(t.palette.text.primary, 0.14),
                        },
                        '& thead th': {
                            fontWeight: 700,
                            borderBottomWidth: 2,
                            borderColor: (t) => alpha(t.palette.text.primary, 0.3),
                        },
                        '& tbody tr': { bgcolor: 'transparent' },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 110 }}>Date</TableCell>
                            <TableCell>Client</TableCell>
                            <TableCell>Job Address</TableCell>
                            <TableCell>Other</TableCell>
                            <TableCell sx={{ width: 70 }}>Start</TableCell>
                            <TableCell sx={{ width: 70 }}>Finish</TableCell>
                            <TableCell align="right" sx={{ width: 80 }}>
                                Hours
                            </TableCell>
                            <TableCell align="right" sx={{ width: 60 }}>
                                1.5×
                            </TableCell>
                            <TableCell align="right" sx={{ width: 60 }}>
                                2×
                            </TableCell>
                            <TableCell align="center" sx={{ width: 60 }}>
                                Lunch
                            </TableCell>
                            <TableCell align="right" sx={{ width: 150 }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groups.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                    <Typography color="text.secondary">
                                        {loading ? 'Loading timesheets...' : `No timesheet entries for ${rangeLabel}.`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            groups.map((g) => {
                                return (
                                    <Fragment key={String(g.staffId)}>
                                        {/* Legacy parent row: the staff name alone, in green. */}
                                        {/* Same background as every other row: only the name and a
                                            heavier line mark where a person starts. */}
                                        <TableRow>
                                            <TableCell
                                                colSpan={11}
                                                sx={{
                                                    pt: 2,
                                                    pb: 0.75,
                                                    borderBottomWidth: '2px !important',
                                                    borderColor: (t) => `${alpha(t.palette.success.main, 0.5)} !important`,
                                                }}
                                            >
                                                <Typography sx={{ fontWeight: 800, color: 'success.main' }}>
                                                    {g.name}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {g.entries.map((e) => {
                                            const address = [e.jobAddress, e.jobSuburb]
                                                .filter((p) => p !== null && p !== '')
                                                .join(', ');
                                            const dayPayNotes = notesByStaffDate.get(`${String(e.staffId)}:${e.workDate}`) ?? [];
                                            const isLeave = e.leaveType !== null;
                                            // Other rows (leave, and legacy free-text like "Public Holiday")
                                            // have no client, job or lunch: blank, not a dash.
                                            const isOther = isLeave || (e.jobId === null && e.jobOther !== null);
                                            return (
                                                <TableRow
                                                    key={e.id}
                                                    hover
                                                    onClick={() => {
                                                        setEditing(e);
                                                    }}
                                                    // One background for every row; the lines separate them.
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            whiteSpace: 'nowrap',
                                                            color: isWeekend(e.workDate) ? 'text.secondary' : undefined,
                                                            fontWeight: multiDay ? 600 : 400,
                                                        }}
                                                    >
                                                        {dayLabel(e.workDate)}
                                                    </TableCell>
                                                    <TableCell>{e.clientName ?? ''}</TableCell>
                                                    <TableCell>
                                                        {address}
                                                    </TableCell>
                                                    <TableCell>
                                                        {isLeave ? (
                                                            <Typography component="span" sx={{ color: 'info.main', fontSize: 'inherit' }}>
                                                                {leaveLabel(e.leaveType)}
                                                            </Typography>
                                                        ) : e.jobId === null ? (
                                                            (e.jobOther ?? '')
                                                        ) : (
                                                            // Clock-on copied the address into job_other on some
                                                            // legacy job rows; the Job Address column already has it.
                                                            ''
                                                        )}
                                                    </TableCell>
                                                    <TableCell>{e.startTime?.slice(0, 5) ?? ''}</TableCell>
                                                    <TableCell>
                                                        {e.isOpen ? (
                                                            <Tooltip title="Clocked on, not clocked off yet">
                                                                <Typography component="span" sx={{ color: 'warning.main', fontSize: 'inherit', whiteSpace: 'nowrap' }}>
                                                                    Clocked on
                                                                </Typography>
                                                            </Tooltip>
                                                        ) : (
                                                            (e.finishTime?.slice(0, 5) ?? '')
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                        {e.isOpen ? (
                                                            ''
                                                        ) : isLeave ? (
                                                            <Box component="span" sx={{ color: 'info.main' }}>
                                                                {blank(e.leaveHours)}
                                                            </Box>
                                                        ) : e.adjustedHours !== null ? (
                                                            <Tooltip
                                                                title={`Adjusted from ${hrs(e.calculatedHours)}${e.adjustedReason === null ? '' : ` — ${e.adjustedReason}`}`}
                                                            >
                                                                <Box component="span" sx={{ color: 'info.main' }}>
                                                                    {blank(e.totalHours)}*
                                                                </Box>
                                                            </Tooltip>
                                                        ) : (
                                                            blank(e.totalHours)
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: 'warning.main' }}>
                                                        {blank(e.overtime15)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: 'error.main' }}>
                                                        {blank(e.overtime2)}
                                                    </TableCell>
                                                    <TableCell align="center">{isOther ? '' : e.lunchTaken ? 'Y' : 'N'}</TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{ whiteSpace: 'nowrap' }}
                                                        onClick={(ev) => {
                                                            ev.stopPropagation();
                                                        }}
                                                    >
                                                        <Tooltip title={e.note === null ? 'No shift note' : 'Shift note'}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="success"
                                                                    disabled={e.note === null}
                                                                    onClick={() => {
                                                                        setShiftNote(e);
                                                                    }}
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={
                                                                dayPayNotes.length === 0
                                                                    ? 'No pay notes for this day'
                                                                    : `${String(dayPayNotes.length)} pay ${dayPayNotes.length === 1 ? 'note' : 'notes'}`
                                                            }
                                                        >
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="info"
                                                                    disabled={dayPayNotes.length === 0}
                                                                    onClick={() => {
                                                                        setDayNotes({ entry: e, notes: dayPayNotes });
                                                                    }}
                                                                >
                                                                    <Badge
                                                                        color="info"
                                                                        variant="dot"
                                                                        invisible={dayPayNotes.length === 0}
                                                                    >
                                                                        <StickyNoteIcon fontSize="small" />
                                                                    </Badge>
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => {
                                                                    setEditing(e);
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={e.invoiced ? 'Invoiced — cannot be deleted' : 'Delete'}>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    disabled={e.invoiced}
                                                                    onClick={() => {
                                                                        setDeleting(e);
                                                                    }}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </Fragment>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                Hours and overtime are worked out from the start and finish times. * hours adjusted by hand. Leave hours are
                shown in blue and are not counted as hours worked.
            </Typography>

            <EntryDialog
                open={editing !== null}
                entry={editing === 'new' ? null : editing}
                staff={staff}
                lookups={lookups}
                defaultStaffId={newFor}
                defaultDate={view.kind === 'day' ? view.date : range.from <= today && today <= range.to ? today : range.from}
                onClose={() => {
                    setEditing(null);
                }}
                onSaved={(message) => {
                    setEditing(null);
                    setNotice(message);
                    reload();
                }}
                onDeleteRequest={(e) => {
                    setDeleting(e);
                }}
            />

            {/* Delete — legacy "Do you really want to delete this record?" */}
            <Dialog
                open={deleting !== null}
                onClose={() => {
                    setDeleting(null);
                }}
                maxWidth="xs"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                <DialogTitle>Delete Entry</DialogTitle>
                <DialogContent dividers>
                    <Typography>
                        Delete {deleting?.staffName ?? 'this'}&apos;s entry for {deleting === null ? '' : dmy(deleting.workDate)}?
                        This cannot be undone.
                    </Typography>
                    {deleting !== null && deleting.leaveRequestId !== null ? (
                        <Typography sx={{ mt: 1, fontSize: '0.85rem', color: 'text.secondary' }}>
                            This is approved leave. Its day on the calendar is removed too.
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        disabled={deleteBusy}
                        onClick={() => {
                            setDeleting(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleteBusy}
                        onClick={() => {
                            if (deleting !== null) {
                                remove(deleting);
                            }
                        }}
                    >
                        {deleteBusy ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Shift note — legacy eye icon */}
            <Dialog
                open={shiftNote !== null}
                onClose={() => {
                    setShiftNote(null);
                }}
                maxWidth="xs"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                <DialogTitle>
                    Shift Note
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                        {shiftNote?.staffName} · {shiftNote === null ? '' : dmy(shiftNote.workDate)}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{shiftNote?.note}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => {
                            setShiftNote(null);
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Pay notes for one day — legacy sticky-note icon, which only ever showed the first note */}
            <Dialog
                open={dayNotes !== null}
                onClose={() => {
                    setDayNotes(null);
                }}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                <DialogTitle>
                    Pay Notes
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                        {dayNotes?.entry.staffName} · {dayNotes === null ? '' : dmy(dayNotes.entry.workDate)}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack gap={1.5}>
                        {(dayNotes?.notes ?? []).map((n) => (
                            <Box key={n.id}>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{n.note}</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {n.updatedBy ?? ''}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    {dayNotes !== null && dayNotes.entry.staffId !== null ? (
                        <Button
                            sx={{ mr: 'auto' }}
                            onClick={() => {
                                if (dayNotes.entry.staffId !== null) {
                                    // From a single day's notes, so the dialog
                                    // opens on that one person.
                                    setPayroll({
                                        staffIds: [dayNotes.entry.staffId],
                                        from: payPeriodFor(dayNotes.entry.workDate, cycle.anchor, cycle.days).from,
                                    });
                                }
                                setDayNotes(null);
                            }}
                        >
                            Open Payroll
                        </Button>
                    ) : null}
                    <Button
                        onClick={() => {
                            setDayNotes(null);
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <PayrollDialog
                open={payroll !== null}
                staffIds={payroll?.staffIds ?? []}
                staffNames={staffNames}
                from={payroll?.from ?? period.from}
                onClose={() => {
                    setPayroll(null);
                    reload();
                }}
                onPeriodChange={(from) => {
                    setPayroll((p) => (p === null ? null : { ...p, from }));
                }}
            />

            <LeaveRequestsDialog
                open={leaveOpen}
                onClose={() => {
                    setLeaveOpen(false);
                }}
                onDecided={() => {
                    reload();
                }}
            />
        </Container>
    );
}
